import * as React from 'react';
import { AutoComplete, Modal, Button, Form, Row, Col } from 'antd';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { Validator } from 'class-validator';

import { DataTableView, ToolbarVisibility, Column } from 'src/core/ui/data-table.view';
import { TextView } from 'src/core/ui/text.view';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { AutocompleteView } from 'src/core/ui/autocomplete/autocomplete.view';
import { RegionEditorController } from 'src/app/general/regions/parts/region-editor.controller';
import { ToastNotifications } from 'src/core/ui/toast';

const FormItem = Form.Item;

interface ContactPartViewProps extends ComponentViewProps<DataSet<ContactItem>> {
    parentModel: Model;
    mode: ControlMode;
    setBusy(isBusy: boolean);
    cityQuery: DropdownRequest<AutocompleteSummary>;
    countyQuery: DropdownRequest<AutocompleteSummary>;
    dataSetCreator<T>(items: T[]): DataSet<T>;
    contactRegions: RegionSummary[];
    search: (postcode: string, callback: (address: Address[]) => void) => void;
	regionSearch: (regionName: string) => Promise<QueryResult<RegionSummary>>;
	hideModificationButton?: boolean;
	selection?: any | false;
}

@observer
export class ContactPartView extends React.Component<ContactPartViewProps, any> {
    dropdownContactRegions: RegionSummary[];

    emailDataSet: DataSet<EmailItem>;

    phoneDataSet: DataSet<PhoneItem>;
    columns: Column[] = [
        {
            key: 'displayName',
            title: 'Display name',
            dataIndex: 'displayName'
        },
        {
            key: 'postalCode',
            title: 'Postal Code',
            dataIndex: 'postalCode'
        },
        {
            key: 'address',
            title: 'Address',
            dataIndex: 'address'
        },
        {
            key: 'city',
            title: 'City',
            dataIndex: 'city'
        },
        {
            key: 'regionId',
            title: 'Region',
            dataIndex: 'regionId',
            render: this.renderContactCell
        }
    ];

    phoneColumns: Column[] = [
        {
            key: 'type',
            dataIndex: 'type',
            editable: (text: any, record: PhoneItem, index: number, isSelected: boolean) => {
                if (isSelected) {
                    return <DropdownView label={null}
                        model={record.type}
                        options={this.phoneOptions}
                        onChanged={newVal => {
                            if (newVal) {
                                record.type = newVal.value;
                            }
                        }}
                    />;
                }
                return record.type || "";
            },
            title: 'Type'
        },
        {
            key: 'number',
            dataIndex: 'number',
            editable: (text: any, record: PhoneItem, index: number, isSelected: boolean) => {
                if (isSelected) {
                    return <TextView mode='edit' label={null}
                        value={record.number}
                        onGetErrorMessage={() => this.props.parentModel.validate(false, 'dynamicProperties')}
                        onChanged={newVal => {
                            record.number = newVal;
                        }}
                    />;
                }
				return record.number || "";
            },
            title: 'Number'
        }
    ];

    emailColumns: Column[] = [
        {
            key: 'type',
            title: 'Type',
            dataIndex: 'type',
            editable: (text: any, record: EmailItem, index: number, isSelected: boolean) => {
                if (isSelected) {
					return (
						<DropdownView label={null}
							model={record.type}
							options={this.emailOptions}
							onChanged={newVal => {
								if (newVal) {
									record.type = newVal.value;
								}
							}}
						/>
					);
                }
				return record.type || "";
            }
        },
        {
            key: 'email',
            title: 'Email',
            dataIndex: 'email',
            editable: (text: any, record: EmailItem, index: number, isSelected: boolean) => {
				if (isSelected) {
					return (
						<TextView
							mode="edit"
							label={null}
							value={record.email}
							onGetErrorMessage={() => this.props.parentModel.validate(false, 'dynamicProperties')}
							onChanged={newVal => {
								record.email = newVal;
							}}
						/>
					);
				}
				return record.email || "";
            },
        }
    ];

    emailOptions = [
        {
            label: 'Personal',
            value: 'Personal'
        },
        {
            label: 'Work',
            value: 'Work'
        }
    ];

    phoneOptions = [
        {
            label: 'Home',
            value: 'Home'
        },
        {
            label: 'Work',
            value: 'Work'
        },
        {
            label: 'Mobile',
            value: 'Mobile'
        },
        {
            label: 'Private mobile',
            value: 'PrivateMobile'
        },
        {
            label: 'Fax',
            value: 'Fax'
        },
        {
            label: 'Private fax',
            value: 'PrivateFax'
        },
        {
            label: 'Emergency phone',
            value: 'EmergencyPhone'
        }
    ];

    @observable
    errorMessage: string = null;

    @observable
    addresses: Address[];

    postcodeTarget: any;
    validator: Validator;
    @resolve
    public toast: ToastNotifications;

	constructor(props) {
		super(props);

		this.validator = new Validator();
		this.state = {
			contact: null,
			isNew: false,
			arraymenu: []
		};
	}

	@autobind
	async isValidPhone(item: PhoneItem) {
		let error = [];
		let phone = this.validator.matches(item.number, /\(?\d+\)?[-.\s]?\d+[-.\s]?\d+$/)
		if (!phone) {
			error.push('The phone number is incorrect');
		}

		return error;
	}

	@autobind
	async isValidEmail(item: EmailItem) {
		let error = [];
		var re = this.validator.isEmail(item.email);
		if (!re) {
			error.push('The email format is incorrect');
		}

		return error;
	}

    render() {
        const contact: ContactItem = this.state.contact;
        const errorMessage: string = this.state.errorMessage;

		return (
			<div>
				{
					contact &&
					<Modal
						title="Edit Contact"
						visible={contact != null}
						onOk={this.confirmDialog}
						onCancel={this.cancelCreate}
						wrapClassName="vertical-center-modal"
						width={820}
						maskClosable={false}
						footer={[
							<Button key='confirm' type="primary" onClick={this.confirmDialog}>Confirm</Button>,
							<Button key='cancel' onClick={this.cancelCreate}>Cancel</Button>]
						}
					>
						<Row>
							<Col span={24}>
								<TextView
									label="Display name"
									onGetErrorMessage={() => this.props.parentModel.validate(false, 'displayName')}
									mode={this.props.mode}
									required
									value={contact.displayName}
									onChanged={newVal => contact.displayName = newVal}
								/>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col xs={24} lg={6}>
								<FormItem label="Postal code" required>
									<AutoComplete
										dataSource={this.state.arraymenu}
										value={contact.postalCode}
										style={{ width: '100%', zIndex: 1001 }}
										placeholder="Postal code"
										onSearch={(value) => {
											contact.postalCode = value;
											this.forceUpdate();
											this.props.search(value, result => {
												this.addresses = result;
												this.buildMenu();
											});
										}}
										onSelect={(value) => {
											let localcountry: string = "";
											let contactLocal: ContactItem = this.state.contact;
											for (let i = 0; i < this.addresses.length; i++) {
												if (value == this.addresses[i].address1) {
													contactLocal.postalCode = this.addresses[i].postalCode;
													value = this.state.contact.postalCode;
													contactLocal.city = this.addresses[i].town;
													contactLocal.county = this.addresses[i].county;
													contactLocal.address = this.addresses[i].address1;
													contactLocal.additionalAddress = this.addresses[i].address2;

													localcountry = this.addresses[i].country;
													this.props.regionSearch(this.addresses[i].country)
														.then(res => {
															this.state.contact.regionId = (res.items.find(e => e.displayName == localcountry) || res.items[0]).id;
															this.addresses = [];
															this.setState({ arraymenu: [], contact: contactLocal });
														});
												}
											}
										}}
									>
									</AutoComplete>
								</FormItem>
							</Col>
							<Col xs={24} lg={6}>
								<RegionEditorController

									label="Region"
									onGetErrorMessage={() => this.validateContact('regionId', contact)}
									mode={this.props.mode}
									onChanged={(value) => { contact.regionId = (value != null ? value.id : null) }}
									value={contact.regionId}
									setBusy={this.props.setBusy}
									afterLoad={this.afterRegionLoad}
								/>
							</Col>
							<Col xs={24} lg={6}>
								<AutocompleteView
									required
									label="City"
									onGetErrorMessage={() => this.validateContact('city', contact)}
									disabled={this.props.mode == 'display'}
									model={contact.city}
									onChanged={newVal => contact.city = newVal.value}
									request={this.props.cityQuery}
								/>
							</Col>
							<Col xs={24} lg={6}>
								<AutocompleteView
									label="County"
									disabled={this.props.mode == 'display'}
									model={contact.county}
									onChanged={newVal => contact.county = newVal.value}
									request={this.props.countyQuery}
								/>
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								<TextView
									required
									onGetErrorMessage={() => this.props.parentModel.validate(false, 'dynamicProperties')}
									mode={this.props.mode}
									label="Address"
									value={contact.address}
									onChanged={newVal => contact.address = newVal}
								/>
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								<TextView
									label="Additional address"
									onGetErrorMessage={() => this.props.parentModel.validate(false, 'dynamicProperties')}
									mode={this.props.mode}
									value={contact.additionalAddress}
									onChanged={newVal => contact.additionalAddress = newVal}
								/>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col xs={24} lg={12}>
								<h3>Additional phones</h3>
								<DataTableView
									columns={this.phoneColumns}
									rowKey="number"
									isValidItem={this.isValidPhone}
									mode={this.props.mode}
									setBusy={this.props.setBusy}
									model={this.phoneDataSet}
									createNewItem={this.newPhone}
									hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination}
								/>
							</Col>
							<Col xs={24} lg={12}>
								<h3>Additional emails</h3>
								<DataTableView
									columns={this.emailColumns}
									rowKey="email"
									isValidItem={this.isValidEmail}
									mode={this.props.mode}
									setBusy={this.props.setBusy}
									model={this.emailDataSet}
									createNewItem={this.newEmail}
									hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination}
								/>
							</Col>
						</Row>
					</Modal>
				}
				<DataTableView
					rowKey="displayName"
					selection={this.props.selection}
					mode={this.props.mode}
					setBusy={this.props.setBusy}
					hideToolbar={this.props.hideModificationButton != null && this.props.hideModificationButton== true ? ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination | ToolbarVisibility.hideFooter : ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination | ToolbarVisibility.hideFooter}
					columns={this.columns}
					model={this.props.model}
					createNewItem={this.newContact}
					itemDoubleClick={(item) => this.editContact(item)}
				/>
			</div>
		);
    }

	@autobind
	async validateContact(field: keyof ContactItem,  item: ContactItem): Promise<ValidationError[]> {

		const errors = this.isContactValid(item);

		var fieldErrors = errors.filter((value, index, arr) => value.property == field);

		return fieldErrors;
	}

    @autobind
    private buildMenu() {
        if (this.addresses && this.addresses.length > 0) {
            let array: any[] = []
            for (let i = 0; i < this.addresses.length; i++) {
                array.push(this.addresses[i].address1);
            }
            this.setState({ arraymenu: array });
        }
    }

	private isContactValid(contact: ContactItem): ValidationError[] {
		let validator = new Validator();
		let message = [];

		if (!validator.isNotEmpty(contact.displayName)) {
			message.push({ text: ' contact displayName is required ', property: 'displayName' });
		}

		if (!validator.isNotEmpty(contact.postalCode)) {
			message.push({ text: ' contact postalCode is required ', property: 'postalCode' });
		}

		if (!validator.isDefined(contact.regionId)) {
			message.push({ text: ' contact regionId is required ', property: "regionId" });
		}

		if (!validator.isNotEmpty(contact.city)) {
			message.push({ text: ' contact city is required ', property: "city" });
		}

		if (!validator.isNotEmpty(contact.address)) {
			message.push({ text: ' contact address is required' , property: 'address'});
		}

		if (contact.displayName != null && !validator.maxLength(contact.displayName, 256)) {
			message.push({ text: ' contact displayName is to long', property: 'displayName' });
		}

		if (contact.city != null && !validator.maxLength(contact.city, 128)) {
			message.push({ text: ' contact city is to long ', property: 'city' });
		}

		if (contact.address != null && !validator.maxLength(contact.address, 512)) {
			message.push({ text: ' contact address is to long', property: 'city' });
		}

		return message;
	}

    @autobind
    private async confirmDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
        const errors = this.isContactValid(this.state.contact);
        if (errors.length == 0) {
            this.closeDialog(ev);
        } else {
            this.toast.showToast('Validation error!', errors.map((item, index) => item.text), 'error');
        }
    }

    @autobind
	private dismiss(): void {
		this.setState({ arraymenu: []})
        this.addresses = null;
    }

    @autobind
    private closeDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
        ev.preventDefault();
        this.emailDataSet = undefined;
        this.phoneDataSet = undefined;
        this.setState({
            contact: undefined,
            isNew: false,
            errorMessage: undefined,
            arraymenu: []
        });
    }

    @autobind
    private cancelCreate(ev?: React.MouseEvent<HTMLButtonElement>) {
        if (this.state.isNew) {
            this.props.model.delete([this.state.contact]);
        }
        this.closeDialog(ev);
    }

	@autobind
	protected newContact() {
		return this.props.model
			.create(() => ({
				displayName: "",
				emails: [],
				phones: []
			}))
			.then(res => {
				this.editContact(res, true);
				return res;
			});
	}

    @autobind
    protected deleteContact(item: any) {
        this.props.model.delete(item);
	}

    @autobind
    protected editContact(item: ContactItem, isNew: boolean = false) {
        if (item.emails == null) {
            item.emails = [];
        }
        if (item.phones == null) {
            item.phones = [];
        }
        this.emailDataSet = this.props.dataSetCreator(item.emails);
        this.phoneDataSet = this.props.dataSetCreator(item.phones);
        this.setState({
            contact: item,
            isNew: isNew,
            errorMessage: undefined
        });
    }

	@autobind
	private newPhone() {
		const contact: ContactItem = this.state.contact;
		const errorMessage: string = this.state.errorMessage;
		const isNew: boolean = this.state.isNew;
		return this.phoneDataSet
			.create(() => ({
				type: 'Work',
				number: ''
			}))
			.then(item => {
				this.setState({
					contact: contact,
					isNew: isNew,
					errorMessage: errorMessage
				});
				return item;
			});
	}

	@autobind
	private newEmail() {
		const contact: ContactItem = this.state.contact;
		const errorMessage: string = this.state.errorMessage;
		const isNew: boolean = this.state.isNew;
		return this.emailDataSet
			.create(() => ({
				type: 'Work',
				email: ''
			}))
			.then(item => {
				this.setState({
					contact: contact,
					isNew: isNew,
					errorMessage: errorMessage
				});
				return item;
			});
	}

    @autobind
    private renderContactCell(text: any, record?: ContactItem, index?: number): any {
        const regions = this.props.contactRegions.slice();
        if (record.regionId && regions.length > 0) {
            let region = regions.find(e => e.id == record.regionId);
            if (region == null) {
                region = this.dropdownContactRegions.find(e => e.id == record.regionId);
                this.props.contactRegions.push(region);
            }
            return <span>{region.displayName}</span>;
        } else {
            return <span></span>;
        }
    }

    @autobind
	private afterRegionLoad(selectedOptions: any, options: any[]): void {
		const old = (this.dropdownContactRegions || []);
		const ids = [
			...old.map(e => e.id),
			...options.map(e => e.id),
			...this.props.contactRegions.map(e => e.id)
		];
		this.dropdownContactRegions = ids.map(x => old.find(e => e.id === x) ||
			this.props.contactRegions.find(e => e.id === x) || options.find(e => e.id === x))
    }
}



// WEBPACK FOOTER //
// ./src/app/crm/contacts/parts/contact-part.view.tsx